import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { submitContactForm } from "../api/contactApi";
import FaqSection from '../components/FaqSection';
import CalendlyEmbed from '../components/CalendlyEmbed';


const AnimatedSection = ({ children, animationVariants, className, triggerOnce, staggerChildren, tag = "div" }) => {
  const MotionComponent = motion[tag]; // Allow specifying the motion component type
  return (
    <MotionComponent
      variants={animationVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: triggerOnce !== false }} // Default triggerOnce to true
      className={className}
      transition={staggerChildren ? { staggerChildren } : undefined}
    >
      {children}
    </MotionComponent>
  );
};

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.5, staggerChildren: 0.1 } },
};

const staggerItemFadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const ContactForm = () => {
  const formRef = useRef();

  const [formValues, setFormValues] = useState({
    "full-name": "",
    company: "",
    email: "",
    "phone-number": "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState({ type: "", message: "" });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Reset form status
    setFormStatus({ type: "", message: "" });
    setIsSubmitting(true);

    const currentForm = formRef.current;
    const formData = {
      fullName: currentForm["full-name"].value,
      company: currentForm["company"].value,
      email: currentForm["email"].value,
      phoneNumber: currentForm["phone-number"].value,
      message: currentForm["message"].value,
    };

    try {
      const response = await submitContactForm(formData);
      console.log("[ContactForm] Submission successful:", response);

      setFormStatus({
        type: "success",
        message: "Thank you for contacting us! We will get back to you soon.",
      });

      currentForm.reset();
      setFormValues({
        "full-name": "",
        company: "",
        email: "",
        "phone-number": "",
        message: "",
      });
    } catch (error) {
      console.error("[ContactForm] Submission error:", error);
      setFormStatus({
        type: "error",
        message: error.message || "Failed to submit form. Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="bg-white flex justify-center py-16 px-4 sm:px-6 lg:py-24 lg:px-8 mt-20">
        <div className="bg-white text-black max-w-6xl w-full">
          <AnimatedSection triggerOnce={false} animationVariants={fadeInUp} className="mb-4">
            <h1 className="text-4xl md:text-5xl font-semibold leading-tight">
              Let's Build the Future <br /> of Finance — Together.
            </h1>
          </AnimatedSection>

          <AnimatedSection triggerOnce={false} animationVariants={fadeInUp} className="mb-12">
            <p className="text-gray-600 text-lg max-w-xl">
              Whether you're a developer exploring our ecosystem, a potential partner, or just curious about what we do — we'd love to hear from you.
            </p>
          </AnimatedSection>

          {/* Form status message */}
          {formStatus.message && (
            <div className={`mb-6 p-4 rounded-lg ${formStatus.type === "success" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
              {formStatus.message}
            </div>
          )}

          <form ref={formRef} onSubmit={handleSubmit}>
            <AnimatedSection triggerOnce={false} animationVariants={fadeIn} staggerChildren={0.1} className="space-y-6">
              <motion.div variants={staggerItemFadeInUp} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Full Name */}
                <div>
                  <input
                    type="text"
                    name="full-name"
                    id="full-name"
                    placeholder="Full Name"
                    required
                    className="w-full bg-gray-50 border border-gray-300 text-black rounded-lg p-4 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                    value={formValues["full-name"]}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  />
                </div>

                {/* Company */}
                <div>
                  <input
                    type="text"
                    name="company"
                    id="company"
                    placeholder="Company"
                    className="w-full bg-gray-50 border border-gray-300 text-black rounded-lg p-4 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                    value={formValues["company"]}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  />
                </div>

                {/* Email */}
                <div>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    placeholder="Email"
                    required
                    className="w-full bg-gray-50 border border-gray-300 text-black rounded-lg p-4 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                    value={formValues["email"]}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  />
                </div>

                {/* Phone Number */}
                <div>
                  <input
                    type="tel"
                    name="phone-number"
                    id="phone-number"
                    placeholder="Phone Number"
                    className="w-full bg-gray-50 border border-gray-300 text-black rounded-lg p-4 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                    value={formValues["phone-number"]}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  />
                </div>
              </motion.div>

              {/* Message Textarea */}
              <motion.div variants={staggerItemFadeInUp}>
                <textarea
                  name="message"
                  id="message"
                  placeholder="Message"
                  rows="6"
                  required
                  className="w-full bg-gray-50 border border-gray-300 text-black rounded-lg p-4 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent resize-none"
                  value={formValues["message"]}
                  onChange={handleChange}
                  disabled={isSubmitting}
                ></textarea>
              </motion.div>

              {/* Submit Button */}
              <motion.div variants={staggerItemFadeInUp}>
                <button
                  type="submit"
                  className="bg-purple-600 text-white py-3 px-8 rounded-lg font-semibold hover:bg-purple-700 transition duration-300 ease-in-out disabled:opacity-70"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Submitting..." : "Submit Now"}
                </button>
              </motion.div>
            </AnimatedSection>
          </form>
        </div>
      </div>
      <div className="bg-gray-50 flex justify-center py-16 px-4">
        <div className="max-w-6xl w-full text-center">
          <h2 className="text-3xl font-semibold text-black">
            Or, Schedule a Meeting Directly
          </h2>

          {/*  calendly embed */}
          <CalendlyEmbed url="https://calendly.com/fode-d-ua/30min" />

        </div>
      </div>
      <FaqSection />

    </>
  );
};

export default ContactForm;
